/**
 * Family Portal API Service
 *
 * API client for family engagement portal endpoints
 */

import type { ApiClient } from '@/core/services';
import type { UUID } from '@fuchsiahomecarecatalin/core/browser';
import type {
  FamilyMember,
  FamilyDashboard,
  ActivityFeedItem,
  Notification,
  MessageThread,
  Message,
  NotificationPreferences,
} from '@fuchsiahomecarecatalin/family-engagement';

const BASE_URL = '/api/family-engagement';

export interface FamilyAccessSettings {
  id?: string;
  familyMemberId: UUID;
  clientId: UUID;
  showVisitUpdates: boolean;
  showCarePlan: boolean;
  showHealthUpdates: boolean;
  showDocuments: boolean;
  allowMessaging: boolean;
  receiveVisitNotifications: boolean;
  visibleDocumentIds: string[];
  restrictedData?: string[];
  updatedBy?: string;
  updatedAt?: string;
}

export interface FamilyAcknowledgement {
  id: string;
  familyMemberId: UUID;
  clientId: UUID;
  itemId: string;
  label: string;
  itemType: string;
  acknowledgedAt: string;
}

export interface FamilyPortalApiService {
  getFamilyMemberProfile(familyMemberId: UUID): Promise<FamilyMember>;
  updateFamilyMemberProfile(familyMemberId: UUID, profile: Partial<FamilyMember>): Promise<FamilyMember>;
  getFamilyDashboard(familyMemberId: UUID): Promise<FamilyDashboard>;
  getRecentActivity(familyMemberId: UUID, limit?: number): Promise<ActivityFeedItem[]>;
  getUnreadNotifications(familyMemberId: UUID): Promise<Notification[]>;
  markNotificationAsRead(notificationId: UUID): Promise<void>;
  getFamilyAccessSettings(familyMemberId: UUID): Promise<FamilyAccessSettings>;
  updateFamilyAccessSettings(
    familyMemberId: UUID,
    settings: Partial<FamilyAccessSettings>
  ): Promise<FamilyAccessSettings>;
  getAcknowledgements(familyMemberId: UUID): Promise<FamilyAcknowledgement[]>;
  acknowledgeItem(
    familyMemberId: UUID,
    payload: { itemId: string; label: string; itemType: string }
  ): Promise<FamilyAcknowledgement>;
  updateNotificationPreferences(
    familyMemberId: UUID,
    preferences: Partial<NotificationPreferences>
  ): Promise<FamilyMember>;
  getMessageThreads(familyMemberId: UUID): Promise<MessageThread[]>;
  getMessagesInThread(threadId: UUID): Promise<Message[]>;
  sendMessage(threadId: UUID, messageText: string): Promise<Message>;
  createMessageThread(
    familyMemberId: UUID,
    clientId: UUID,
    subject: string,
    initialMessage: string
  ): Promise<MessageThread>;
}

export const createFamilyPortalApiService = (apiClient: ApiClient): FamilyPortalApiService => {
  return {
    /**
     * Get family member profile with statistics
     */
    async getFamilyMemberProfile(familyMemberId: UUID): Promise<FamilyMember> {
      return apiClient.get<FamilyMember>(`${BASE_URL}/family-members/${familyMemberId}`);
    },

    async updateFamilyMemberProfile(
      familyMemberId: UUID,
      profile: Partial<FamilyMember>
    ): Promise<FamilyMember> {
      return apiClient.patch<FamilyMember>(`${BASE_URL}/family-members/${familyMemberId}`, profile);
    },

    /**
     * Get family dashboard data
     */
    async getFamilyDashboard(familyMemberId: UUID): Promise<FamilyDashboard> {
      return apiClient.get<FamilyDashboard>(`${BASE_URL}/dashboard/family-member/${familyMemberId}`);
    },

    /**
     * Get recent activity feed
     */
    async getRecentActivity(familyMemberId: UUID, limit = 20): Promise<ActivityFeedItem[]> {
      return apiClient.get<ActivityFeedItem[]>(
        `${BASE_URL}/activity-feed/family-member/${familyMemberId}?limit=${limit}`
      );
    },

    /**
     * Get unread notifications
     */
    async getUnreadNotifications(familyMemberId: UUID): Promise<Notification[]> {
      return apiClient.get<Notification[]>(
        `${BASE_URL}/notifications/family-member/${familyMemberId}/unread`
      );
    },

    /**
     * Mark notification as read
     */
    async markNotificationAsRead(notificationId: UUID): Promise<void> {
      await apiClient.patch<void>(`${BASE_URL}/notifications/${notificationId}/read`);
    },

    async getFamilyAccessSettings(familyMemberId: UUID): Promise<FamilyAccessSettings> {
      return apiClient.get<FamilyAccessSettings>(
        `${BASE_URL}/family-members/${familyMemberId}/access-settings`
      );
    },

    async updateFamilyAccessSettings(
      familyMemberId: UUID,
      settings: Partial<FamilyAccessSettings>
    ): Promise<FamilyAccessSettings> {
      return apiClient.patch<FamilyAccessSettings>(
        `${BASE_URL}/family-members/${familyMemberId}/access-settings`,
        settings
      );
    },

    async getAcknowledgements(familyMemberId: UUID): Promise<FamilyAcknowledgement[]> {
      return apiClient.get<FamilyAcknowledgement[]>(
        `${BASE_URL}/acknowledgements/family-member/${familyMemberId}`
      );
    },

    async acknowledgeItem(
      familyMemberId: UUID,
      payload: { itemId: string; label: string; itemType: string }
    ): Promise<FamilyAcknowledgement> {
      return apiClient.post<FamilyAcknowledgement>(
        `${BASE_URL}/acknowledgements/family-member/${familyMemberId}`,
        payload
      );
    },

    /**
     * Update notification preferences
     */
    async updateNotificationPreferences(
      familyMemberId: UUID,
      preferences: Partial<NotificationPreferences>
    ): Promise<FamilyMember> {
      return apiClient.patch<FamilyMember>(
        `${BASE_URL}/family-members/${familyMemberId}/preferences`,
        preferences
      );
    },

    /**
     * Get message threads for family member
     */
    async getMessageThreads(familyMemberId: UUID): Promise<MessageThread[]> {
      return apiClient.get<MessageThread[]>(
        `${BASE_URL}/messages/family-member/${familyMemberId}/threads`
      );
    },

    /**
     * Get messages in a thread
     */
    async getMessagesInThread(threadId: UUID): Promise<Message[]> {
      return apiClient.get<Message[]>(`${BASE_URL}/messages/threads/${threadId}/messages`);
    },

    /**
     * Send message in thread
     */
    async sendMessage(threadId: UUID, messageText: string): Promise<Message> {
      return apiClient.post<Message>(`${BASE_URL}/messages/threads/${threadId}/messages`, {
        messageText,
        senderType: 'FAMILY',
      });
    },

    /**
     * Create new message thread
     */
    async createMessageThread(
      familyMemberId: UUID,
      clientId: UUID,
      subject: string,
      initialMessage: string
    ): Promise<MessageThread> {
      return apiClient.post<MessageThread>(`${BASE_URL}/messages/threads`, {
        familyMemberId,
        clientId,
        subject,
        initialMessage,
        priority: 'NORMAL',
      });
    },
  };
};
